/**
 * Organization profile state for the four org settings pages — company
 * profile, team, branding and sub-contexts.
 *
 * Same idiom as `state/threadSettings.ts`: a per-org cached atom over the
 * workspace server's `/api/amoreg/org-profile`, which forwards to the domain
 * API (`apps/api`) where the database work happens. Mutations POST a patch
 * of only the fields a page owns, then refresh.
 *
 * @module state/orgProfile
 */
import {
  DEFAULT_INSPECTION_SCHEDULE,
  type InspectionSchedule,
} from "@repo/shared/inspectionSchedule";
import { useAtomValue } from "@effect/atom-react";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useMemo } from "react";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";

const SubContextSchema = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  context: Schema.String,
});
export type OrgSubContext = typeof SubContextSchema.Type;

const BrandingSchema = Schema.Struct({
  primaryColor: Schema.NullOr(Schema.String),
  secondaryColor: Schema.NullOr(Schema.String),
  accentColor: Schema.NullOr(Schema.String),
  brandGuidelines: Schema.NullOr(Schema.String),
  toneOfVoice: Schema.NullOr(Schema.String),
  logoUrl: Schema.NullOr(Schema.String),
  logoDarkUrl: Schema.NullOr(Schema.String),
});
export type OrgBranding = typeof BrandingSchema.Type;

/**
 * A member on the wire. The picture, title and department are optional keys
 * so a profile from a server that predates them still decodes;
 * `parseProfileSnapshot` fills the nulls.
 */
const TeamMemberSchema = Schema.Struct({
  id: Schema.String,
  email: Schema.String,
  name: Schema.NullOr(Schema.String),
  role: Schema.String,
  joinedAt: Schema.NullOr(Schema.String),
  avatarUrl: Schema.optionalKey(Schema.NullOr(Schema.String)),
  jobTitle: Schema.optionalKey(Schema.NullOr(Schema.String)),
  department: Schema.optionalKey(Schema.NullOr(Schema.String)),
  /** Optional on the wire so a server that predates them still decodes. */
  modelAllowanceGbp: Schema.optionalKey(Schema.NullOr(Schema.Number)),
  onDemandModelUsage: Schema.optionalKey(Schema.Boolean),
});
type WireTeamMember = typeof TeamMemberSchema.Type;

/** A member as the team page renders them: every detail present, null when unknown. */
export interface OrgTeamMember {
  readonly id: string;
  readonly email: string;
  readonly name: string | null;
  readonly role: string;
  readonly joinedAt: string | null;
  readonly avatarUrl: string | null;
  readonly jobTitle: string | null;
  readonly department: string | null;
  /** Included model usage per calendar month, in pounds; null = the platform default. Amoreg sets it. */
  readonly modelAllowanceGbp: number | null;
  /** Whether the member may keep using models past their allowance, billed on demand. */
  readonly onDemandModelUsage: boolean;
}

/** An older server sends none of the optional fields: nothing known, platform default, on-demand usage on. */
const toTeamMember = (member: WireTeamMember): OrgTeamMember => ({
  id: member.id,
  email: member.email,
  name: member.name,
  role: member.role,
  joinedAt: member.joinedAt,
  avatarUrl: member.avatarUrl ?? null,
  jobTitle: member.jobTitle ?? null,
  department: member.department ?? null,
  modelAllowanceGbp: member.modelAllowanceGbp ?? null,
  onDemandModelUsage: member.onDemandModelUsage ?? true,
});

const InvitationSchema = Schema.Struct({
  id: Schema.String,
  email: Schema.String,
  role: Schema.Literals(["admin", "member"]),
  invitedByEmail: Schema.NullOr(Schema.String),
  createdAt: Schema.String,
  expiresAt: Schema.String,
});
export type OrgInvitation = typeof InvitationSchema.Type;

/**
 * The wire shape. The invitation fields are optional keys so a profile from
 * a server that predates them (or a fixture that omits them) still decodes;
 * `parseProfileSnapshot` fills the defaults. Like `logoDarkUrl` in orgs.ts.
 */
const ProfileSchema = Schema.Struct({
  organizationId: Schema.String,
  name: Schema.String,
  slug: Schema.NullOr(Schema.String),
  organizationContext: Schema.NullOr(Schema.String),
  regulatoryMonitoringEnabled: Schema.Boolean,
  /** Optional on the wire so a server that predates it still decodes. */
  inspectionSchedule: Schema.optionalKey(
    Schema.Struct({
      cadence: Schema.Literals(["off", "daily", "weekly"]),
      hourUtc: Schema.Number,
      weekday: Schema.Number,
    }),
  ),
  branding: BrandingSchema,
  subContexts: Schema.Array(SubContextSchema),
  team: Schema.Array(TeamMemberSchema),
  /** Outstanding invitations; the API lists them for managers only. */
  invitations: Schema.optionalKey(Schema.Array(InvitationSchema)),
  viewerRole: Schema.NullOr(Schema.String),
  canManage: Schema.Boolean,
  /** Whether the deployment emails invitations; otherwise the admin shares a link. */
  invitationEmailEnabled: Schema.optionalKey(Schema.Boolean),
});
type WireProfile = typeof ProfileSchema.Type;

export interface OrgProfileSnapshot extends Omit<
  WireProfile,
  "team" | "invitations" | "invitationEmailEnabled" | "inspectionSchedule"
> {
  readonly invitations: ReadonlyArray<OrgInvitation>;
  readonly invitationEmailEnabled: boolean;
  readonly inspectionSchedule: InspectionSchedule;
  readonly team: ReadonlyArray<OrgTeamMember>;
}

const decodeProfile = Schema.decodeUnknownEffect(ProfileSchema);

/** Decode a profile payload, defaulting the fields an older server may omit. */
export const parseProfileSnapshot = (json: unknown): Effect.Effect<OrgProfileSnapshot, unknown> =>
  decodeProfile(json).pipe(
    Effect.map((wire): OrgProfileSnapshot => ({
      ...wire,
      team: wire.team.map(toTeamMember),
      invitations: wire.invitations ?? [],
      invitationEmailEnabled: wire.invitationEmailEnabled ?? false,
      inspectionSchedule: wire.inspectionSchedule ?? DEFAULT_INSPECTION_SCHEDULE,
    })),
  );

const InviteReceiptSchema = Schema.Struct({
  invitation: InvitationSchema,
  delivery: Schema.Literals(["email", "link"]),
});
const decodeInviteReceipt = Schema.decodeUnknownSync(InviteReceiptSchema);

/** The API answers failures with `{message}`; fall back to the body, then a default. */
async function failureMessage(response: Response, fallback: string): Promise<string> {
  const text = await response.text().catch(() => "");
  try {
    const parsed = JSON.parse(text) as { message?: string };
    return parsed.message ?? fallback;
  } catch {
    return text.length > 0 ? text : fallback;
  }
}

export type InviteResult =
  | {
      readonly ok: true;
      readonly invitation: OrgInvitation;
      /** `email` when the deployment sent one; `link` when the admin passes the sign-in link on. */
      readonly delivery: "email" | "link";
    }
  | { readonly ok: false; readonly message: string };

/**
 * Invite an address into the organization. Exported for tests and for the
 * hook below; the API owns the admin gate and the email send.
 */
export async function inviteToOrganization(
  organizationId: string,
  payload: { readonly email: string; readonly role: "admin" | "member" },
): Promise<InviteResult> {
  const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/org-profile/invitations", {
    organizationId,
  });
  try {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      return {
        ok: false,
        message: await failureMessage(response, "The invitation could not be sent."),
      };
    }
    const receipt = decodeInviteReceipt(await response.json());
    return { ok: true, invitation: receipt.invitation, delivery: receipt.delivery };
  } catch {
    return { ok: false, message: "The invitation could not be sent." };
  }
}

/** Withdraw an outstanding invitation; resolves to an error message, or null. */
export async function revokeOrganizationInvitation(
  organizationId: string,
  invitationId: string,
): Promise<string | null> {
  const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/org-profile/invitations/revoke", {
    organizationId,
    invitationId,
  });
  try {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: "{}",
    });
    if (!response.ok) {
      return failureMessage(response, "The invitation could not be withdrawn.");
    }
    return null;
  } catch {
    return "The invitation could not be withdrawn.";
  }
}

/**
 * Switch a member's on-demand model usage on or off; the API owns the
 * admin gate. Resolves to an error message, or null.
 */
export async function setOrganizationMemberOnDemandUsage(
  organizationId: string,
  userId: string,
  onDemandModelUsage: boolean,
): Promise<string | null> {
  const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/org-profile/member", {
    organizationId,
    userId,
    action: "model-usage",
  });
  try {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ onDemandModelUsage }),
    });
    if (!response.ok) {
      return failureMessage(response, "The change could not be saved.");
    }
    return null;
  } catch {
    return "The change could not be saved.";
  }
}

/**
 * Set a member's included model allowance (pounds per month; null restores
 * the platform default). Amoreg operators only — the API refuses
 * organization admins. Resolves to an error message, or null.
 */
export async function setOrganizationMemberModelAllowance(
  organizationId: string,
  userId: string,
  modelAllowanceGbp: number | null,
): Promise<string | null> {
  const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/org-profile/member", {
    organizationId,
    userId,
    action: "model-allowance",
  });
  try {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ modelAllowanceGbp }),
    });
    if (!response.ok) {
      return failureMessage(response, "The allowance could not be saved.");
    }
    return null;
  } catch {
    return "The allowance could not be saved.";
  }
}

/**
 * The link an admin hands to an invitee when email is off: the workspace
 * itself. Signing in with the invited address redeems the invitation, so
 * the link carries nothing secret.
 */
export function invitationSignInLink(origin: string): string {
  return `${origin.replace(/\/+$/, "")}/`;
}

/** Patch shape: only the fields the calling page owns. */
export interface OrgProfilePatch {
  readonly organizationContext?: string | null;
  /**
   * The profile this edit started from. Send it alongside a change and the
   * server refuses rather than overwriting a version the editor never saw —
   * eve can append to this column while the dialog sits open.
   */
  readonly expectedOrganizationContext?: string | null;
  readonly regulatoryMonitoringEnabled?: boolean;
  readonly inspectionSchedule?: InspectionSchedule;
  readonly branding?: {
    readonly primaryColor?: string | null;
    readonly secondaryColor?: string | null;
    readonly accentColor?: string | null;
    readonly brandGuidelines?: string | null;
    readonly toneOfVoice?: string | null;
  };
  readonly subContexts?: ReadonlyArray<OrgSubContext>;
}

function profileUrl(organizationId: string): string {
  return resolvePrimaryEnvironmentHttpUrl("/api/amoreg/org-profile", { organizationId });
}

/** Why a settings page has nothing to show — each gets its own sentence. */
export type OrgProfileUnavailableReason =
  | "unconfigured"
  | "no-identity"
  | "idp-unconfigured"
  | "error";

export const UNAVAILABLE_MESSAGES: Readonly<Record<OrgProfileUnavailableReason, string>> = {
  unconfigured:
    "This server isn’t connected to the Amoreg domain API, so these settings can’t be loaded. Set AMOREG_API_SERVICE_TOKEN and AMOREG_API_URL on the server.",
  "no-identity":
    "This session isn’t signed in through Amoreg, so it has no profile to manage. Sign in with your email on the sign-in page.",
  "idp-unconfigured":
    "The domain API has no identity-provider key (WORKOS_API_KEY), so sign-ins can’t be listed. Set it on the API deployment.",
  error: "These settings couldn’t be loaded. Try again in a moment.",
};

/** The server answers 404 with a machine-readable body naming the cause. */
export function reasonFromBody(body: string): OrgProfileUnavailableReason {
  if (body.includes("domain-api-unconfigured")) return "unconfigured";
  if (body.includes("no-workos-identity")) return "no-identity";
  // The domain API's own words when WORKOS_API_KEY is missing there.
  if (body.includes("Identity provider is not configured")) return "idp-unconfigured";
  return "error";
}

const fetchProfile = (organizationId: string) =>
  Effect.gen(function* () {
    if (organizationId.length === 0) {
      return null;
    }
    const response = yield* Effect.tryPromise(() =>
      fetch(profileUrl(organizationId), { credentials: "include" }),
    );
    if (!response.ok) {
      const body = yield* Effect.tryPromise(() => response.text()).pipe(
        Effect.orElseSucceed(() => ""),
      );
      return { unavailable: reasonFromBody(body) } as const;
    }
    const json = yield* Effect.tryPromise(() => response.json());
    return yield* parseProfileSnapshot(json);
  }).pipe(Effect.orElseSucceed(() => null));

const profileRuntime = Atom.runtime(Layer.empty);
const profileAtom = Atom.family((organizationId: string) =>
  profileRuntime
    .atom(fetchProfile(organizationId))
    .pipe(Atom.withLabel(`web-amoreg:org-profile:${organizationId}`)),
);

export type OrgMemberAction =
  | { readonly kind: "role"; readonly role: "admin" | "member" }
  | { readonly kind: "remove" };

export interface OrgProfileView {
  readonly profile: OrgProfileSnapshot | null;
  /** Set when there is no profile, naming why. */
  readonly unavailableReason: OrgProfileUnavailableReason | null;
  /** False when the server has no domain API, or the session has no platform user. */
  readonly available: boolean;
  readonly isPending: boolean;
  readonly canManage: boolean;
  readonly refresh: () => void;
  /** POST a patch; resolves to an error message, or null on success. */
  readonly save: (patch: OrgProfilePatch) => Promise<string | null>;
  /** Change a membership role or end it; resolves to an error message, or null. */
  readonly applyMemberAction: (userId: string, action: OrgMemberAction) => Promise<string | null>;
  /** Invite an address; the profile refreshes with the new pending row. */
  readonly invite: (payload: {
    readonly email: string;
    readonly role: "admin" | "member";
  }) => Promise<InviteResult>;
  /** Withdraw an outstanding invitation; resolves to an error message, or null. */
  readonly revokeInvitation: (invitationId: string) => Promise<string | null>;
  /** Switch a member's on-demand model usage; resolves to an error message, or null. */
  readonly setMemberOnDemandUsage: (userId: string, enabled: boolean) => Promise<string | null>;
  /** Set a member's included allowance (operators only); resolves to an error message, or null. */
  readonly setMemberModelAllowance: (
    userId: string,
    allowanceGbp: number | null,
  ) => Promise<string | null>;
}

export function useOrgProfile(organizationId: string | null): OrgProfileView {
  const atom = useMemo(() => profileAtom(organizationId ?? ""), [organizationId]);
  const result = useAtomValue(atom);

  const refresh = useCallback(() => {
    appAtomRegistry.refresh(atom);
  }, [atom]);

  const save = useCallback(
    async (patch: OrgProfilePatch): Promise<string | null> => {
      if (!organizationId) return "No organization selected.";
      try {
        const response = await fetch(profileUrl(organizationId), {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(patch),
        });
        if (!response.ok) {
          const text = await response.text().catch(() => "");
          const message = (() => {
            try {
              const parsed = JSON.parse(text) as { message?: string };
              return parsed.message ?? null;
            } catch {
              return text.length > 0 ? text : null;
            }
          })();
          return message ?? "The change could not be saved.";
        }
        appAtomRegistry.refresh(atom);
        return null;
      } catch {
        return "The change could not be saved.";
      }
    },
    [atom, organizationId],
  );

  const applyMemberAction = useCallback(
    async (userId: string, action: OrgMemberAction): Promise<string | null> => {
      if (!organizationId) return "No organization selected.";
      const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/org-profile/member", {
        organizationId,
        userId,
        action: action.kind,
      });
      try {
        const response = await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(action.kind === "role" ? { role: action.role } : {}),
        });
        if (!response.ok) {
          const text = await response.text().catch(() => "");
          try {
            const parsed = JSON.parse(text) as { message?: string };
            return parsed.message ?? "The change could not be saved.";
          } catch {
            return text.length > 0 ? text : "The change could not be saved.";
          }
        }
        appAtomRegistry.refresh(atom);
        return null;
      } catch {
        return "The change could not be saved.";
      }
    },
    [atom, organizationId],
  );

  const invite = useCallback(
    async (payload: { readonly email: string; readonly role: "admin" | "member" }) => {
      if (!organizationId) return { ok: false as const, message: "No organization selected." };
      const outcome = await inviteToOrganization(organizationId, payload);
      if (outcome.ok) appAtomRegistry.refresh(atom);
      return outcome;
    },
    [atom, organizationId],
  );

  const revokeInvitation = useCallback(
    async (invitationId: string): Promise<string | null> => {
      if (!organizationId) return "No organization selected.";
      const failure = await revokeOrganizationInvitation(organizationId, invitationId);
      if (failure === null) appAtomRegistry.refresh(atom);
      return failure;
    },
    [atom, organizationId],
  );

  const setMemberOnDemandUsage = useCallback(
    async (userId: string, enabled: boolean): Promise<string | null> => {
      if (!organizationId) return "No organization selected.";
      const failure = await setOrganizationMemberOnDemandUsage(organizationId, userId, enabled);
      if (failure === null) appAtomRegistry.refresh(atom);
      return failure;
    },
    [atom, organizationId],
  );

  const setMemberModelAllowance = useCallback(
    async (userId: string, allowanceGbp: number | null): Promise<string | null> => {
      if (!organizationId) return "No organization selected.";
      const failure = await setOrganizationMemberModelAllowance(
        organizationId,
        userId,
        allowanceGbp,
      );
      if (failure === null) appAtomRegistry.refresh(atom);
      return failure;
    },
    [atom, organizationId],
  );

  return useMemo(() => {
    const value = Option.getOrNull(AsyncResult.value(result)) ?? null;
    const profile = value !== null && "unavailable" in value ? null : value;
    const unavailableReason = value !== null && "unavailable" in value ? value.unavailable : null;
    return {
      profile,
      unavailableReason,
      available: profile !== null,
      isPending: result.waiting && value === null,
      canManage: profile?.canManage ?? false,
      refresh,
      save,
      applyMemberAction,
      invite,
      revokeInvitation,
      setMemberOnDemandUsage,
      setMemberModelAllowance,
    };
  }, [
    applyMemberAction,
    invite,
    refresh,
    result,
    revokeInvitation,
    save,
    setMemberModelAllowance,
    setMemberOnDemandUsage,
  ]);
}
